import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play, SkipForward, RotateCcw, MonitorUp, X, Volume2, VolumeX, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChgkTimer } from '../../../hooks/useChgkTimer';
import mainSignalSound from '../../../assets/sound/main-signal.mp3';
import preAlarmSound from '../../../assets/sound/pre-alarm.mp3';
import { playSoundCue, preloadSoundCues } from '../../../utils/soundCues';
import TimerSoundSettingsDialog from './TimerSoundSettingsDialog';
import {
  CONTROL_LABELS,
  DEFAULT_TIMER_SOUND_SETTINGS,
  MODE_LABELS,
  STEP_ORDINALS,
  TimerCompletePayload,
  TimerDurationsInput,
  TimerMode,
  TimerQuestionChangePayload,
  TimerSoundCue,
  TimerSoundSettings,
  TimerState,
  WRITING_ANSWER_SECONDS,
  getDurationMsForMode,
} from '../../../const/timer';

type TimerVariant = 'fullscreen' | 'widget';

export interface ChgkTimerController {
  start: () => void;
  pause: () => void;
  toggleStartPause: () => void;
  nextQuestion: () => void;
  resetCurrent: () => void;
  switchMode: (mode: TimerMode) => void;
}

interface ChgkTimerProps {
  variant?: TimerVariant;
  className?: string;
  title?: string;
  initialMode?: TimerMode;
  durations?: TimerDurationsInput;
  enableKeyboardShortcuts?: boolean;
  enableSoundCue?: boolean;
  showQuestionNumber?: boolean;
  controllerRef?: React.MutableRefObject<ChgkTimerController | null>;
  onClose?: () => void;
  onModeChange?: (mode: TimerMode) => void;
  onTimerComplete?: (payload: TimerCompletePayload) => void;
  onQuestionChange?: (payload: TimerQuestionChangePayload) => void;
  onStateChange?: (state: TimerState) => void;
}

const MUTE_STORAGE_KEY = '3sual.timer.muted';
const SOUND_SETTINGS_STORAGE_KEY = '3sual.timer.sounds';

const SOUND_CUE_SOURCES: Record<TimerSoundCue, string> = {
  start: mainSignalSound,
  preAlarm: preAlarmSound,
  mainEnd: mainSignalSound,
  writingEnd: mainSignalSound,
};

const readSoundSettings = (): TimerSoundSettings => {
  try {
    const stored = JSON.parse(localStorage.getItem(SOUND_SETTINGS_STORAGE_KEY) ?? 'null');
    return { ...DEFAULT_TIMER_SOUND_SETTINGS, ...(stored && typeof stored === 'object' ? stored : {}) };
  } catch {
    return DEFAULT_TIMER_SOUND_SETTINGS;
  }
};

const RING_RADIUS = 42;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS; // ~263.9

export const ChgkTimer: React.FC<ChgkTimerProps> = ({
  variant = 'widget',
  className,
  title = 'NHN Taymer',
  initialMode = TimerMode.QUESTION,
  durations,
  enableKeyboardShortcuts = variant === 'fullscreen',
  enableSoundCue = true,
  showQuestionNumber = false,
  controllerRef,
  onClose,
  onModeChange,
  onTimerComplete,
  onQuestionChange,
  onStateChange,
}) => {
  // Mute is a per-device preference, so it survives reloads of the timer.
  const [muted, setMuted] = useState<boolean>(() => {
    try {
      return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleMuted = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      try {
        localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // storage unavailable (private mode) — keep the in-memory value
      }
      return next;
    });
  }, []);

  // Per-cue on/off choices, also a per-device preference.
  const [soundSettings, setSoundSettings] = useState<TimerSoundSettings>(readSoundSettings);
  const [soundSettingsOpen, setSoundSettingsOpen] = useState(false);

  const toggleSoundCue = useCallback((cue: TimerSoundCue) => {
    setSoundSettings(prev => {
      const next = { ...prev, [cue]: !prev[cue] };
      try {
        localStorage.setItem(SOUND_SETTINGS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // storage unavailable (private mode) — keep the in-memory value
      }
      return next;
    });
  }, []);

  const playSound = useCallback((cue: TimerSoundCue) => {
    if (!enableSoundCue || muted || !soundSettings[cue]) return;
    playSoundCue(SOUND_CUE_SOURCES[cue]);
  }, [enableSoundCue, muted, soundSettings]);

  useEffect(() => {
    if (enableSoundCue) preloadSoundCues(mainSignalSound, preAlarmSound);
  }, [enableSoundCue]);

  const onComplete = useCallback((payload: TimerCompletePayload) => {
    // Signal when the main time runs out and again when the writing time ends.
    if (payload.phase === 'writing') {
      playSound('writingEnd');
    } else if (payload.isFinalBlitzStep) {
      playSound('mainEnd');
    }
    onTimerComplete?.(payload);
  }, [onTimerComplete, playSound]);

  const {
    questionPhase,
    mode,
    state,
    remainingMs,
    remainingSeconds,
    questionNumber,
    dupletStep,
    dupletStepsTotal,
    blitzStep,
    blitzStepsTotal,
    durations: resolvedDurations,
    isRunning,
    switchMode,
    start,
    pause,
    nextQuestion,
    nextAndStart,
    resetCurrent,
  } = useChgkTimer({
    initialMode,
    durations,
    onModeChange,
    onTimerComplete: onComplete,
    onQuestionChange,
    onStateChange,
  });

  const handleStartPause = useCallback(() => {
    if (isRunning) {
      pause();
      return;
    }
    playSound('start');
    start();
  }, [isRunning, pause, playSound, start]);

  const preAlarmStateRef = useRef<{ questionNumber: number; played: boolean }>({
    questionNumber: -1,
    played: false,
  });

  useEffect(() => {
    if (mode !== TimerMode.QUESTION || questionPhase !== 'main') {
      preAlarmStateRef.current = { questionNumber, played: false };
      return;
    }
    if (preAlarmStateRef.current.questionNumber !== questionNumber) {
      preAlarmStateRef.current = { questionNumber, played: false };
    }
    if (isRunning && remainingSeconds === 10 && !preAlarmStateRef.current.played) {
      playSound('preAlarm');
      preAlarmStateRef.current.played = true;
    }
  }, [isRunning, mode, playSound, questionNumber, questionPhase, remainingSeconds]);

  useEffect(() => {
    if (!controllerRef) return;
    controllerRef.current = {
      start: () => {
        if (isRunning) return;
        playSound('start');
        start();
      },
      pause,
      toggleStartPause: handleStartPause,
      nextQuestion,
      resetCurrent,
      switchMode,
    };
    return () => { controllerRef.current = null; };
  }, [controllerRef, handleStartPause, isRunning, nextQuestion, pause, playSound, resetCurrent, start, switchMode]);

  useEffect(() => {
    if (!enableKeyboardShortcuts) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return;
      if (soundSettingsOpen) return;
      if (event.code === 'Space') {
        event.preventDefault();
        handleStartPause();
      } else if (event.code === 'Enter') {
        event.preventDefault();
        nextQuestion();
      } else if (event.code === 'KeyM') {
        event.preventDefault();
        toggleMuted();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enableKeyboardShortcuts, handleStartPause, nextQuestion, soundSettingsOpen, toggleMuted]);

  const modeDurationMs = useMemo(
    () => getDurationMsForMode(mode, resolvedDurations),
    [mode, resolvedDurations]
  );
  const phaseDurationMs = useMemo(
    () => (questionPhase === 'writing' ? WRITING_ANSWER_SECONDS * 1000 : modeDurationMs),
    [mode, modeDurationMs, questionPhase]
  );
  const progressRatio = phaseDurationMs > 0 ? Math.max(0, Math.min(1, remainingMs / phaseDurationMs)) : 0;
  const strokeDashoffset = RING_CIRCUMFERENCE * (1 - progressRatio);

  const urgencyTone = remainingSeconds <= 5 ? 'critical' : remainingSeconds <= 10 ? 'warning' : 'normal';

  const ringColor =
    urgencyTone === 'critical' ? '#ef4444' :
    urgencyTone === 'warning'  ? '#fbbf24' :
    '#38bdf8';

  const ringGlow =
    urgencyTone === 'critical' ? 'rgba(239,68,68,0.55)' :
    urgencyTone === 'warning'  ? 'rgba(251,191,36,0.45)' :
    'rgba(56,189,248,0.40)';

  const isBlitz = mode === TimerMode.BLITZ;
  const isDuplet = mode === TimerMode.DUPLET;
  const isWritingPhase = questionPhase === 'writing';
  const isQuestionFinished = isWritingPhase && state === TimerState.FINISHED;
  const phaseLabel = state === TimerState.FINISHED
    ? CONTROL_LABELS.timeUp
    : isWritingPhase ? CONTROL_LABELS.writeAnswer : CONTROL_LABELS.discussion;

  // ── Fullscreen ───────────────────────────────────────────────────────────────
  if (variant === 'fullscreen') {
    const ghostBtnStyle: React.CSSProperties = {
      borderRadius: '0.75rem',
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.10)',
    };

    const primaryBtnClass =
      'inline-flex min-h-[52px] w-full items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-white transition sm:w-auto';

    // Duplet / Blitz primary action
    const multiStepPrimaryBtn = (() => {
      if (state === TimerState.FINISHED && questionPhase === 'writing') {
        return (
          <button
            type="button"
            onClick={() => switchMode(TimerMode.QUESTION)}
            style={ghostBtnStyle}
            className={cn(primaryBtnClass, 'min-w-[180px] hover:bg-white/10')}
          >
            <SkipForward className="h-4 w-4" />
            {CONTROL_LABELS.nextQuestion}
          </button>
        );
      }
      const currentSubStep = isDuplet ? dupletStep : blitzStep;
      const totalSubSteps  = isDuplet ? dupletStepsTotal : blitzStepsTotal;
      const isLastSubStep  = currentSubStep >= totalSubSteps;
      const labelIndex     = state === TimerState.FINISHED
        ? (isLastSubStep ? 0 : currentSubStep)
        : currentSubStep - 1;
      const stepLabel = `${STEP_ORDINALS[labelIndex] ?? `${labelIndex + 1}.`} sualı başlat`;

      return isRunning ? (
        <button
          type="button"
          onClick={pause}
          style={{ borderRadius: '0.75rem' }}
          className={cn(primaryBtnClass, 'min-w-[140px] bg-amber-500 hover:bg-amber-600')}
        >
          <Pause className="h-4 w-4" />
          {CONTROL_LABELS.pause}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => {
            playSound('start');
            if (state === TimerState.FINISHED) nextAndStart();
            else start();
          }}
          style={{ borderRadius: '0.75rem' }}
          className={cn(primaryBtnClass, 'min-w-[200px] bg-sky-500 hover:bg-sky-600')}
        >
          <Play className="h-4 w-4" />
          {stepLabel}
        </button>
      );
    })();

    return (
      <section
        className={cn('fixed inset-0 z-[120] overflow-y-auto flex flex-col text-white', className)}
        style={{ background: 'radial-gradient(ellipse at 50% 38%, #0c1e33 0%, #040b14 100%)' }}
      >
        {/* Subtle grid */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(56,189,248,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.035) 1px, transparent 1px)',
            backgroundSize: '52px 52px',
          }}
        />

        {/* ── Header ── */}
        <header className="relative z-10 flex flex-col gap-4 px-4 pb-3 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:gap-2 sm:px-10 sm:pb-2 sm:pt-[calc(1.75rem+env(safe-area-inset-top))]">
          {/* Row 1: Title + Close */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center sm:h-10 sm:w-10"
                style={{
                  borderRadius: '0.75rem',
                  background: 'rgba(56,189,248,0.10)',
                  border: '1px solid rgba(56,189,248,0.18)',
                }}
              >
                <MonitorUp className="h-4 w-4 text-sky-400 sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <span className="block truncate text-base font-black tracking-tight text-sky-300 leading-tight sm:text-xl">{title}</span>
                {enableKeyboardShortcuts && (
                  <p className="hidden text-[11px] leading-tight sm:block" style={{ color: 'rgba(186,230,253,0.45)' }}>
                    {CONTROL_LABELS.keyboardHint}
                  </p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {enableSoundCue && (
                <button
                  type="button"
                  onClick={toggleMuted}
                  aria-pressed={muted}
                  title={muted ? CONTROL_LABELS.muteOff : CONTROL_LABELS.muteOn}
                  aria-label={muted ? CONTROL_LABELS.muteOff : CONTROL_LABELS.muteOn}
                  style={ghostBtnStyle}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center transition sm:h-10 sm:w-10',
                    muted ? 'text-amber-400 hover:text-amber-300' : 'text-white/60 hover:text-white'
                  )}
                >
                  {muted ? <VolumeX className="h-4 w-4 sm:h-5 sm:w-5" /> : <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />}
                </button>
              )}
              {enableSoundCue && (
                <button
                  type="button"
                  onClick={() => setSoundSettingsOpen(true)}
                  title={CONTROL_LABELS.soundSettings}
                  aria-label={CONTROL_LABELS.soundSettings}
                  style={ghostBtnStyle}
                  className="flex h-9 w-9 items-center justify-center text-white/60 transition hover:text-white sm:h-10 sm:w-10"
                >
                  <Settings2 className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Bağla"
                  style={ghostBtnStyle}
                  className="flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 text-sm font-semibold text-white/60 transition hover:text-white sm:h-10 sm:w-auto sm:px-3"
                >
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">Bağla</span>
                </button>
              )}
            </div>
          </div>

          {/* Row 2: Mode pills */}
          <div className="flex justify-center">
            <div
              className="flex items-center gap-1"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '0.875rem',
                padding: '0.3rem',
              }}
            >
              {(Object.values(TimerMode) as TimerMode[]).map((item) => {
                const active = item === mode;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => switchMode(item)}
                    aria-pressed={active}
                    style={{
                      borderRadius: '0.625rem',
                      transition: 'all 0.18s ease',
                      boxShadow: active ? '0 0 14px rgba(14,165,233,0.45)' : undefined,
                    }}
                    className={cn(
                      'px-3 py-1.5 text-sm font-bold sm:px-4 sm:py-2',
                      active ? 'bg-sky-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
                    )}
                  >
                    {MODE_LABELS[item]}
                  </button>
                );
              })}
            </div>
          </div>
        </header>

        {/* ── Main ── */}
        <main className="relative z-10 flex flex-1 flex-col items-center justify-center gap-7 px-4 pb-8">
          {/* Ambient glow disc */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute"
            style={{
              width: 'min(52vmin, 420px)',
              height: 'min(52vmin, 420px)',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${ringGlow} 0%, transparent 68%)`,
              transition: 'background 0.7s ease',
              filter: 'blur(24px)',
            }}
          />

          {/* Ring + number */}
          <div
            className="relative shrink-0"
            style={{ width: 'min(60vmin, 490px)', height: 'min(60vmin, 490px)' }}
          >
            {/* SVG progress ring — viewBox padded by 8px on each side so glow never clips */}
            <svg
              viewBox="-8 -8 116 116"
              className="w-full h-full"
              style={{ transform: 'rotate(-90deg)' }}
              aria-hidden="true"
            >
              {/* Track */}
              <circle
                cx="50" cy="50" r={RING_RADIUS}
                fill="none"
                stroke="rgba(148,163,184,0.07)"
                strokeWidth="5"
              />
              {/* Wide outer glow — blur via feGaussianBlur so it's never clipped */}
              <circle
                cx="50" cy="50" r={RING_RADIUS}
                fill="none"
                stroke={ringColor}
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={strokeDashoffset}
                opacity="0.18"
                style={{ transition: 'stroke-dashoffset 0.08s linear, stroke 0.5s ease' }}
              />
              {/* Mid glow layer */}
              <circle
                cx="50" cy="50" r={RING_RADIUS}
                fill="none"
                stroke={ringColor}
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={strokeDashoffset}
                opacity="0.30"
                style={{ transition: 'stroke-dashoffset 0.08s linear, stroke 0.5s ease' }}
              />
              {/* Primary arc */}
              <circle
                cx="50" cy="50" r={RING_RADIUS}
                fill="none"
                stroke={ringColor}
                strokeWidth="4.5"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={strokeDashoffset}
                style={{ transition: 'stroke-dashoffset 0.08s linear, stroke 0.5s ease' }}
              />
            </svg>

            {/* Center content overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {/* Inner dark disc */}
              <div
                aria-hidden="true"
                className="absolute"
                style={{
                  width: '72%',
                  height: '72%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(4,11,20,0.97) 55%, transparent 100%)',
                }}
              />

              {/* Timer number */}
              <span
                className="relative font-black leading-none"
                style={{
                  fontSize: 'clamp(4.5rem, 17vmin, 11rem)',
                  color: ringColor,
                  transition: 'color 0.5s ease',
                  textShadow: `0 0 28px ${ringGlow}, 0 0 56px ${ringGlow}`,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.02em',
                }}
              >
                {remainingSeconds}
              </span>

              {/* Keep the phase label short enough to fit inside the ring. */}
              <p
                className="relative mt-2 max-w-[60%] text-center text-sm font-semibold leading-snug"
                style={{ color: 'rgba(186,230,253,0.80)' }}
              >
                {phaseLabel}
              </p>

              {/* Step dots — shown inside the ring for duplet/blitz */}
              {(isDuplet || isBlitz) && (
                <div className="relative mt-2.5 flex items-center gap-2">
                  {Array.from({ length: isDuplet ? dupletStepsTotal : blitzStepsTotal }).map((_, i) => {
                    const currentStep = (isDuplet ? dupletStep : blitzStep) - 1; // 0-based
                    const filled = i <= currentStep;
                    return (
                      <div
                        key={i}
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: filled ? ringColor : 'rgba(148,163,184,0.20)',
                          boxShadow: filled ? `0 0 6px ${ringColor}` : undefined,
                          transition: 'background 0.3s ease, box-shadow 0.3s ease',
                        }}
                      />
                    );
                  })}
                </div>
              )}

              {/* Question number (when shown) */}
              {showQuestionNumber && (
                <p
                  className="relative mt-2 text-xs font-semibold"
                  style={{ color: 'rgba(186,230,253,0.50)' }}
                >
                  {CONTROL_LABELS.question} <span style={{ color: ringColor }}>{questionNumber}</span>
                </p>
              )}
            </div>
          </div>

          {/* Both panels occupy the same grid cell and reserve their wrapped height,
              keeping the controls steady as the timer changes phase. */}
          <div role="status" aria-atomic="true" className="grid w-full max-w-sm shrink-0 text-center font-semibold break-words">
            <div
              aria-hidden={!isWritingPhase || isQuestionFinished}
              className={cn(
                'col-start-1 row-start-1 flex flex-col gap-1',
                (!isWritingPhase || isQuestionFinished) && 'invisible'
              )}
            >
              <p className="text-xs leading-5" style={{ color: 'rgba(251,191,36,0.85)' }}>
                {CONTROL_LABELS.discussionTimeUp}
              </p>
              <p className="text-sm leading-5" style={{ color: 'rgba(186,230,253,0.80)' }}>
                {CONTROL_LABELS.writingAnswer}
              </p>
            </div>
            <p
              aria-hidden={!isQuestionFinished}
              className={cn('col-start-1 row-start-1 self-center text-sm leading-5', !isQuestionFinished && 'invisible')}
              style={{ color: 'rgba(186,230,253,0.80)' }}
            >
              {CONTROL_LABELS.questionTimeUp}
            </p>
          </div>

          {/* ── Controls ── */}
          <div className="flex flex-col items-stretch gap-2 w-full max-w-xs sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-3 sm:max-w-none">
            {/* Primary: start/pause */}
            {(isDuplet || isBlitz) ? multiStepPrimaryBtn : (
              <button
                type="button"
                onClick={handleStartPause}
                style={{ borderRadius: '0.75rem' }}
                className={cn(
                  primaryBtnClass,
                  'min-w-[148px]',
                  isRunning ? 'bg-amber-500 hover:bg-amber-600' : 'bg-sky-500 hover:bg-sky-600'
                )}
              >
                {isRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {isRunning ? CONTROL_LABELS.pause : CONTROL_LABELS.start}
              </button>
            )}

            {/* Next sub-step (duplet/blitz mid-sequence) */}
            {(isDuplet || isBlitz) && questionPhase === 'main' &&
             (state === TimerState.RUNNING || state === TimerState.PAUSED) &&
             (isDuplet ? dupletStep < dupletStepsTotal : blitzStep < blitzStepsTotal) && (
              <button
                type="button"
                onClick={nextQuestion}
                style={ghostBtnStyle}
                className={cn(primaryBtnClass, 'min-w-[180px] hover:bg-white/10')}
              >
                <SkipForward className="h-4 w-4" />
                {isDuplet ? CONTROL_LABELS.nextDupletQuestion : CONTROL_LABELS.nextBlitzQuestion}
              </button>
            )}

            {/* Next question (standard mode) */}
            {!(isDuplet || isBlitz) && (
              <button
                type="button"
                onClick={nextQuestion}
                style={ghostBtnStyle}
                className={cn(primaryBtnClass, 'min-w-[180px] hover:bg-white/10')}
              >
                <SkipForward className="h-4 w-4" />
                {CONTROL_LABELS.nextQuestion}
              </button>
            )}

            {/* Reset */}
            <button
              type="button"
              onClick={resetCurrent}
              disabled={state === TimerState.IDLE && remainingMs === phaseDurationMs}
              style={ghostBtnStyle}
              className={cn(
                primaryBtnClass,
                'min-w-[150px] hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35'
              )}
            >
              <RotateCcw className="h-4 w-4" />
              {CONTROL_LABELS.reset}
            </button>
          </div>

        </main>
        <TimerSoundSettingsDialog
          open={soundSettingsOpen}
          onOpenChange={setSoundSettingsOpen}
          settings={soundSettings}
          onToggle={toggleSoundCue}
        />
      </section>
    );
  }

  // ── Widget variant ────────────────────────────────────────────────────────────
  const widgetRingColor =
    urgencyTone === 'critical' ? '#ef4444' :
    urgencyTone === 'warning'  ? '#f59e0b' :
    '#2563eb';

  const widgetRingGlow =
    urgencyTone === 'critical' ? 'rgba(239,68,68,0.35)' :
    urgencyTone === 'warning'  ? 'rgba(245,158,11,0.30)' :
    'rgba(37,99,235,0.25)';

  const widgetGhostBtnStyle: React.CSSProperties = {
    borderRadius: '0.625rem',
    border: '1px solid rgba(37,99,235,0.15)',
    background: 'rgba(37,99,235,0.05)',
  };

  // Duplet / Blitz primary action (widget)
  const widgetMultiStepPrimary = (() => {
    if (state === TimerState.FINISHED && questionPhase === 'writing') {
      return (
        <button
          type="button"
          onClick={() => switchMode(TimerMode.QUESTION)}
          style={widgetGhostBtnStyle}
          className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 dark:text-blue-300 transition hover:bg-blue-50 dark:hover:bg-blue-950"
        >
          <SkipForward className="h-3.5 w-3.5" />
          {CONTROL_LABELS.nextQuestion}
        </button>
      );
    }
    const currentSubStep = isDuplet ? dupletStep : blitzStep;
    const totalSubSteps  = isDuplet ? dupletStepsTotal : blitzStepsTotal;
    const isLastSubStep  = currentSubStep >= totalSubSteps;
    const labelIndex     = state === TimerState.FINISHED
      ? (isLastSubStep ? 0 : currentSubStep)
      : currentSubStep - 1;
    const stepLabel = `${STEP_ORDINALS[labelIndex] ?? `${labelIndex + 1}.`} sualı başlat`;

    return isRunning ? (
      <button
        type="button"
        onClick={pause}
        style={{ borderRadius: '0.625rem' }}
        className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 bg-amber-500 px-3 py-2 text-xs font-bold text-white transition hover:bg-amber-600"
      >
        <Pause className="h-3.5 w-3.5" />
        {CONTROL_LABELS.pause}
      </button>
    ) : (
      <button
        type="button"
        onClick={() => {
          playSound('start');
          if (state === TimerState.FINISHED) nextAndStart();
          else start();
        }}
        style={{ borderRadius: '0.625rem' }}
        className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 bg-blue-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-blue-700"
      >
        <Play className="h-3.5 w-3.5" />
        {stepLabel}
      </button>
    );
  })();

  return (
    <div
      className={cn('rounded-2xl p-4 text-slate-900 dark:text-gray-50', className)}
      style={{
        border: '1px solid rgba(37,99,235,0.12)',
        background: 'linear-gradient(145deg, #ffffff 0%, #f0f6ff 100%)',
        boxShadow: '0 4px 24px rgba(37,99,235,0.08)',
      }}
    >
      {/* Widget header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className="flex h-8 w-8 items-center justify-center"
            style={{
              borderRadius: '0.5rem',
              background: 'rgba(37,99,235,0.08)',
              border: '1px solid rgba(37,99,235,0.14)',
            }}
          >
            <MonitorUp className="h-4 w-4 text-blue-600" />
          </div>
          <span className="text-sm font-black tracking-tight text-blue-900">{title}</span>
          {enableSoundCue && (
            <button
              type="button"
              onClick={toggleMuted}
              aria-pressed={muted}
              title={muted ? CONTROL_LABELS.muteOff : CONTROL_LABELS.muteOn}
              aria-label={muted ? CONTROL_LABELS.muteOff : CONTROL_LABELS.muteOn}
              style={{ borderRadius: '0.5rem' }}
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center transition',
                muted
                  ? 'text-amber-600 hover:bg-amber-50'
                  : 'text-blue-600/60 hover:bg-blue-50 hover:text-blue-700'
              )}
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          )}
          {enableSoundCue && (
            <button
              type="button"
              onClick={() => setSoundSettingsOpen(true)}
              title={CONTROL_LABELS.soundSettings}
              aria-label={CONTROL_LABELS.soundSettings}
              style={{ borderRadius: '0.5rem' }}
              className="flex h-7 w-7 shrink-0 items-center justify-center text-blue-600/60 transition hover:bg-blue-50 hover:text-blue-700"
            >
              <Settings2 className="h-4 w-4" />
            </button>
          )}
        </div>
        {/* Mode pills */}
        <div
          className="flex items-center gap-0.5"
          style={{
            background: 'rgba(37,99,235,0.06)',
            border: '1px solid rgba(37,99,235,0.12)',
            borderRadius: '0.625rem',
            padding: '0.2rem',
          }}
        >
          {(Object.values(TimerMode) as TimerMode[]).map((item) => {
            const active = item === mode;
            return (
              <button
                key={item}
                type="button"
                onClick={() => switchMode(item)}
                aria-pressed={active}
                style={{
                  borderRadius: '0.4rem',
                  transition: 'all 0.15s ease',
                }}
                className={cn(
                  'px-2.5 py-1 text-[11px] font-bold',
                  active
                    ? 'bg-blue-600 text-white'
                    : 'text-blue-600/60 hover:text-blue-700 hover:bg-blue-50'
                )}
              >
                {MODE_LABELS[item]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Widget ring */}
      <div className="flex flex-col items-center gap-4">
        <div
          className="relative"
          style={{ width: 'min(72vw, 220px)', height: 'min(72vw, 220px)' }}
        >
          <svg
            viewBox="0 0 100 100"
            className="w-full h-full"
            style={{ transform: 'rotate(-90deg)' }}
            aria-hidden="true"
          >
            <circle
              cx="50" cy="50" r={RING_RADIUS}
              fill="none"
              stroke="rgba(37,99,235,0.07)"
              strokeWidth="5.5"
            />
            <circle
              cx="50" cy="50" r={RING_RADIUS}
              fill="none"
              stroke={widgetRingColor}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
              opacity="0.12"
              style={{ transition: 'stroke-dashoffset 0.08s linear, stroke 0.4s ease' }}
            />
            <circle
              cx="50" cy="50" r={RING_RADIUS}
              fill="none"
              stroke={widgetRingColor}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: 'stroke-dashoffset 0.08s linear, stroke 0.4s ease',
                filter: `drop-shadow(0 0 4px ${widgetRingColor})`,
              }}
            />
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div
              aria-hidden="true"
              className="absolute"
              style={{
                width: '72%',
                height: '72%',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(240,246,255,0.98) 55%, transparent 100%)',
              }}
            />
            <span
              className="relative font-black leading-none"
              style={{
                fontSize: 'clamp(2.8rem, 9vw, 4.5rem)',
                color: widgetRingColor,
                transition: 'color 0.4s ease',
                textShadow: `0 0 18px ${widgetRingGlow}`,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
              }}
            >
              {remainingSeconds}
            </span>
            <p className="relative mt-1 text-[11px] font-semibold text-center px-3 text-slate-500 leading-tight">
              {questionPhase === 'writing' ? CONTROL_LABELS.writingAnswer : MODE_LABELS[mode]}
            </p>
          </div>
        </div>

        {(state === TimerState.WRITING_ANSWER || state === TimerState.FINISHED) && (
          <p className="text-center text-xs font-semibold text-blue-600">
            {CONTROL_LABELS.questionTimeUp}
          </p>
        )}

        {/* Controls */}
        <div className="flex w-full flex-wrap items-center justify-center gap-2">
          {(isDuplet || isBlitz) ? widgetMultiStepPrimary : (
            <button
              type="button"
              onClick={handleStartPause}
              style={{ borderRadius: '0.625rem' }}
              className={cn(
                'inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white transition',
                isRunning ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'
              )}
            >
              {isRunning ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              {isRunning ? CONTROL_LABELS.pause : CONTROL_LABELS.start}
            </button>
          )}

          {(isDuplet || isBlitz) && questionPhase === 'main' &&
           (state === TimerState.RUNNING || state === TimerState.PAUSED) &&
           (isDuplet ? dupletStep < dupletStepsTotal : blitzStep < blitzStepsTotal) && (
            <button
              type="button"
              onClick={nextQuestion}
              style={widgetGhostBtnStyle}
              className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 transition hover:bg-blue-50"
            >
              <SkipForward className="h-3.5 w-3.5" />
              {isDuplet ? CONTROL_LABELS.nextDupletQuestion : CONTROL_LABELS.nextBlitzQuestion}
            </button>
          )}

          {!(isDuplet || isBlitz) && (
            <button
              type="button"
              onClick={nextQuestion}
              style={widgetGhostBtnStyle}
              className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 transition hover:bg-blue-50"
            >
              <SkipForward className="h-3.5 w-3.5" />
              {CONTROL_LABELS.nextQuestion}
            </button>
          )}

          <button
            type="button"
            onClick={resetCurrent}
            disabled={state === TimerState.IDLE && remainingMs === phaseDurationMs}
            style={{
              borderRadius: '0.625rem',
              border: '1px solid rgba(100,116,139,0.15)',
              background: 'rgba(100,116,139,0.05)',
            }}
            className="inline-flex min-h-[40px] items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {CONTROL_LABELS.reset}
          </button>
        </div>

        {/* Step info */}
        {(showQuestionNumber || isDuplet || isBlitz) && (
          <div
            className="flex w-full items-center justify-center gap-3 rounded-xl px-3 py-2 text-xs"
            style={{
              background: 'rgba(37,99,235,0.05)',
              border: '1px solid rgba(37,99,235,0.10)',
              color: 'rgba(71,85,105,0.8)',
            }}
          >
            {showQuestionNumber && (
              <span>
                {CONTROL_LABELS.question}{' '}
                <strong className="font-bold text-blue-700">{questionNumber}</strong>
              </span>
            )}
            {isDuplet && (
              <span>
                {CONTROL_LABELS.dupletStep}{' '}
                <strong className="font-bold text-blue-700">{dupletStep}/{dupletStepsTotal}</strong>
              </span>
            )}
            {isBlitz && (
              <span>
                {CONTROL_LABELS.blitzStep}{' '}
                <strong className="font-bold text-blue-700">{blitzStep}/{blitzStepsTotal}</strong>
              </span>
            )}
          </div>
        )}
      </div>
      <TimerSoundSettingsDialog
        open={soundSettingsOpen}
        onOpenChange={setSoundSettingsOpen}
        settings={soundSettings}
        onToggle={toggleSoundCue}
      />
    </div>
  );
};

export const ChgkTimerWidget: React.FC<Omit<ChgkTimerProps, 'variant'>> = (props) => (
  <ChgkTimer variant="widget" {...props} />
);

export default ChgkTimer;
